import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {ControlEventMessage, TopologyStatus} from '@mintdata-wsp/common/controllers/flow/FlowCloudController';
import {
    SimulationId,
    SimulationStatus,
    SimulationStopReason
} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';

export class FlowSimulationStatusController
{
    public setStatus(simulationId:SimulationId, newStatus:SimulationStatus, reason?:SimulationStopReason)
    {
        const {flowSimulation} = commonControllers;

        if( newStatus === SimulationStatus.Stopped )
        {
            flowSimulation.setSimulationStatusToStopped(simulationId, reason);
        }
        else
        {
            flowSimulation.setSimulationStatus(simulationId, newStatus);
        }
    }

    public onUserStartingTopology(simulationId:SimulationId):void
    {
        this.setStatus(simulationId, SimulationStatus.Starting);
    }

    public onUserStoppingTopology(simulationId:SimulationId):void
    {
        this.setStatus(simulationId, SimulationStatus.Stopping);
    }

    public onUserStoppedTopology(simulationId:SimulationId):void
    {
        this.setStatus(simulationId, SimulationStatus.Stopped, SimulationStopReason.UserStoppedTopology);
    }

    public onValidationErrors(simulationId:SimulationId):void
    {
        this.setStatus(simulationId, SimulationStatus.Stopped, SimulationStopReason.FlowValidationErrors);
    }

    public onFailedToStartErrors(simulationId:SimulationId):void
    {
        this.setStatus(simulationId, SimulationStatus.Stopped, SimulationStopReason.CloudFailedToStart);
    }

    public onControlMessageFromCloud(simulationId:SimulationId, message:ControlEventMessage):void
    {
        const nextState = this._computeNextStateForControl(simulationId, message);

        if( nextState )
        {
            this.setStatus(simulationId, nextState.status, nextState.reason);
        }
    }

    public onDataMessageFromCloud(simulationId:SimulationId, frame:IFrame):void
    {
        const nextState = this._computeNextStateForData(simulationId);

        if( nextState )
        {
            this.setStatus(simulationId, nextState.status, nextState.reason);
        }
    }

    public onErrorFromCloud(simulationId:SimulationId, data:any):void
    {
        this.setStatus(simulationId, SimulationStatus.Stopped, SimulationStopReason.ErrorOverSSEChannel);
    }

    private _computeNextStateForData(simulationId:SimulationId):{status:SimulationStatus, reason:any} | null
    {
        const {flowSimulation} = commonControllers;
        const status = flowSimulation.getSimulationStatus(simulationId);

        if( status === SimulationStatus.Starting ||
            status === SimulationStatus.WaitingForFirstVisualData )
        {
            return {status: SimulationStatus.Running, reason: null};
        }
        else
        {
            return null;
        }
    }

    private _computeNextStateForControl(simulationId:SimulationId, msg:ControlEventMessage):{status:SimulationStatus, reason:any} | null
    {
        const topologyState = msg.state;
        const {flowSimulation} = commonControllers;

        switch( topologyState )
        {
            case TopologyStatus.Allocated:
            case TopologyStatus.Submitted:
                return {status: SimulationStatus.Starting, reason: null};
            case TopologyStatus.Running:
            {
                const status = flowSimulation.getSimulationStatus(simulationId);
                const isStarting = status === SimulationStatus.Starting;

                if( isStarting )
                    return {status: SimulationStatus.WaitingForFirstVisualData, reason: null};
                else
                    return null;
            }
            case TopologyStatus.Starving:
                // Do nothing. This is a transient state; cloud will follow-up with
                // one of the following statuses at a subsequent time:
                //
                // Running
                // ConnectionLoss
                // Stopping
                // Terminated
                //
                return null;
            case TopologyStatus.Connection_Loss:
                // Do nothing. This is a transient state; cloud will follow-up with
                // one of the following statuses at a subsequent time:
                //
                // Running
                // ConnectionLoss
                // Stopping
                // Terminated
                //
                return null;
            case TopologyStatus.Stopping:
            case TopologyStatus.Terminated:
                return {status: SimulationStatus.Stopped, reason: SimulationStopReason.CloudTerminated};
        }
    }
}
